import {
    Box,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    SxProps,
    Theme,
    Typography,
    useTheme,
} from "@mui/material";
import React from "react";
import { renderNodeRule, StructuredText } from "react-datocms";
import {
    isLink,
    isParagraph,
    isHeading,
    isList,
    isListItem,
    Node,
    List as ListNode,
} from "datocms-structured-text-utils";
import {
    Document as StructuredTextDocument,
    Record as StructuredTextGraphQlResponseRecord,
    StructuredText as StructuredTextGraphQlResponse,
} from "datocms-structured-text-utils/dist/types/types";
import { MetaEntry } from "datocms-structured-text-utils/src/types";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Responsive } from "components/ThemeWrapper";

export type StyledStructuredTextPropTypes<
    R1 extends StructuredTextGraphQlResponseRecord,
    C extends React.ElementType,
    R2 extends StructuredTextGraphQlResponseRecord = R1,
> = {
    data: StructuredTextGraphQlResponse<R1, R2> | StructuredTextDocument | Node | null | undefined;
    component?: C;
    variant?: "body1" | "caption" | undefined;
    sx?: SxProps<Theme>;
};

const asObject = (items?: Array<MetaEntry>) => {
    let obj: any = {};
    items?.forEach((item) => (obj[item.id] = item.value));
    return obj;
};

export const StyledStructuredText: <
    R1 extends StructuredTextGraphQlResponseRecord,
    C extends React.ElementType,
    R2 extends StructuredTextGraphQlResponseRecord = R1,
>(
    props: StyledStructuredTextPropTypes<R1, C, R2>,
) => React.ReactElement<StyledStructuredTextPropTypes<R1, C, R2>> = ({ data, component, variant = "body1", sx }) => {
    const theme = useTheme();
    return (
        <Box
            component={component || "div"}
            sx={{
                "p:is(:last-child)": {
                    mb: 0,
                },
                "p + ul": {
                    mt: Responsive("-24px", "-32px"),
                },
            }}
        >
            <StructuredText
                data={data}
                customNodeRules={[
                    renderNodeRule(isParagraph, ({ children, key, ancestors }) => {
                        return (
                            <Typography variant={variant} gutterBottom={!isListItem(ancestors[0])} key={key} sx={sx}>
                                {children}
                            </Typography>
                        );
                    }),
                    renderNodeRule(isHeading, ({ node, children, key }) => {
                        return (
                            <Typography variant={`h${node.level}`} gutterBottom key={key}>
                                {children}
                            </Typography>
                        );
                    }),
                    renderNodeRule(isLink, ({ node, children, key }) => {
                        return (
                            <Link key={key} href={node.url} {...asObject(node.meta)}>
                                {children}
                            </Link>
                        );
                    }),
                    renderNodeRule(isList, ({ children, key }) => {
                        return <List key={key}>{children}</List>;
                    }),
                    renderNodeRule(isListItem, ({ node, children, key, ancestors }) => {
                        const list = ancestors.find((a) => a.type === "list") as ListNode;

                        if (list?.style === "bulleted") {
                            return (
                                <ListItem key={key}>
                                    <ListItemIcon sx={{ fontSize: Responsive(21, 24) }}>
                                        <FontAwesomeIcon
                                            icon={faChevronRight}
                                            style={{ fontSize: "inherit", color: theme.palette.primary.main }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText>{children}</ListItemText>
                                </ListItem>
                            );
                        } else {
                            return (
                                <ListItem key={key} sx={{ alignItems: "flex-start" }}>
                                    <ListItemIcon sx={{ mt: "4px", mb: "4px" }}>
                                        <Typography variant={"body1"} key={key}>
                                            {list.children.indexOf(node) + 1}.
                                        </Typography>
                                    </ListItemIcon>
                                    <ListItemText>{children}</ListItemText>
                                </ListItem>
                            );
                        }
                    }),
                ]}
            />
        </Box>
    );
};
